import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector, batch } from 'react-redux';

import { isEmpty } from 'lodash';
import { format } from 'date-fns';

import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@material-ui/core';

import {
  WellnubTable,
  WellnubTimeInput,
  WellnubCheckboxInput,
} from '@wellnub/web-common';

import Button from '../../Button/Button.component';

import { DAYS } from 'utils/constants';

import { setScheduleTimeId } from 'redux/slices/client.slice';
import { setModalStatus } from 'redux/slices/modal.slice';

import { useUpdate } from 'hooks/user.hook';

const days = [
  { label: 'Lu', value: 1 },
  { label: 'Ma', value: 2 },
  { label: 'Mi', value: 3 },
  { label: 'Ju', value: 4 },
  { label: 'Vi', value: 5 },
  { label: 'Sa', value: 6 },
  { label: 'Do', value: 0 },
];

// Toda la sección "Mi Perfil" del partner es de solo lectura: los cambios se
// piden a Wellnub, no se editan aquí. Se deja como constante y no borrando el
// código de guardado para que revertirlo sea una línea, y para que las ramas
// apagadas sigan referenciando sus handlers.
const IS_READ_ONLY = true;

const WorkdaysScheduleTable = ({ dirtyHandler }) => {
  const [data, setData] = useState([]);
  const [error, setError] = useState(false);

  const [isAllDaySelected, setIsAllDaySelected] = useState(false);

  const { inbodyScheduleTimes } = useSelector(state => state.client);

  const { mutateAsync, isLoading } = useUpdate();

  const {
    getValues,
    control,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      days: {
        0: false,
        1: false,
        2: false,
        3: false,
        4: false,
        5: false,
        6: false,
      },
      startTime: new Date(2022, 0, 1, 0, 0),
      endTime: new Date(2022, 0, 1, 23, 59),
    },
  });

  const dispatch = useDispatch();

  const HEADERS = ['Acciones', 'Horario', 'Días de la semana'];

  const ACTIONS = [
    {
      type: 'DELETE',
      title: 'Eliminar',
      handler: id => {
        batch(() => {
          dispatch(
            setModalStatus({
              isActive: true,
              name: 'DELETE_SCHEDULE_OPEN_TIME',
            }),
          );
        });
        dispatch(setScheduleTimeId(id));
      },
    },
  ];

  const onAllDaySelectedChangeHandler = () => {
    setIsAllDaySelected(prevState => !prevState);
  };

  const onSaveHandler = () => {
    const { startTime, endTime, days } = getValues();
    const mappedDays = Object.keys(days)
      .filter(key => days[key])
      .map(value => +value);
    if (isEmpty(mappedDays)) {
      setError(true);
    } else {
      setError(false);
      const payload = [
        ...inbodyScheduleTimes.map(item => item.data),
        {
          days: mappedDays,
          startTime: isAllDaySelected
            ? format(new Date(2022, 0, 1, 0, 0), 'HH:mm')
            : format(startTime, 'HH:mm'),
          endTime: isAllDaySelected
            ? format(new Date(2022, 0, 1, 23, 59), 'HH:mm')
            : format(endTime, 'HH:mm'),
        },
      ];
      mutateAsync({
        inbodyScheduleTimes: payload,
      })
        .then(() => {
          reset();
          setIsAllDaySelected(false);
        })
        .catch(e => {});
    }
  };

  useEffect(() => {
    if (!isEmpty(inbodyScheduleTimes)) {
      const mappedTimes = inbodyScheduleTimes.map(item => ({
        ...item,
        data: {
          schedule: `${item.data.startTime} - ${item.data.endTime}`,
          days: [...item.data.days]
            .sort()
            .map(day => DAYS[day])
            .join(' - '),
        },
      }));
      setData(mappedTimes);
    } else {
      setData([]);
    }
  }, [inbodyScheduleTimes]);

  useEffect(() => {
    dirtyHandler(isDirty);
  }, [isDirty, dirtyHandler]);

  return (
    <Box>
      <WellnubTable
        headers={HEADERS}
        actions={ACTIONS}
        rows={data}
        disabled={IS_READ_ONLY}
      />
      <Grid container spacing={2} style={{ marginTop: '16px' }}>
        <Grid item xs={6} sm={2} style={{ textAlign: 'center' }}>
          <Typography variant="h6">Horario Inicio</Typography>
          <WellnubTimeInput
            fullWidth
            name="startTime"
            control={control}
            inputVariant="outlined"
            disabled={IS_READ_ONLY || isAllDaySelected}
            rules={{
              required: 'Este campo es requerido',
            }}
          />
        </Grid>
        <Grid item xs={6} sm={2} style={{ textAlign: 'center' }}>
          <Typography variant="h6">Horario Fin</Typography>
          <WellnubTimeInput
            fullWidth
            name="endTime"
            control={control}
            inputVariant="outlined"
            disabled={IS_READ_ONLY || isAllDaySelected}
            rules={{
              required: 'Este campo es requerido',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} style={{ textAlign: 'center' }}>
          <Typography variant="h6">Días de la semana</Typography>
          <Grid container>
            {days.map(day => (
              <Grid item xs key={day.label}>
                <WellnubCheckboxInput
                  control={control}
                  label={day.label}
                  name={`days.${day.value}`}
                  disabled={IS_READ_ONLY}
                />
              </Grid>
            ))}
          </Grid>
          <Box>
            {error && (
              <Typography style={{ color: '#ff4262' }}>
                Selecciona una opción para continuar
              </Typography>
            )}
          </Box>
          {/*<WellnubCheckboxGroupInput*/}
          {/*  row*/}
          {/*  fullWidth*/}
          {/*  name="days"*/}
          {/*  control={control}*/}
          {/*  options={WEEK_OPTIONS}*/}
          {/*  rules={{*/}
          {/*    required: 'Este campo es requerido',*/}
          {/*  }}*/}
          {/*  helperText=" "*/}
          {/*/>*/}
        </Grid>
        {!IS_READ_ONLY && (
          <Grid
            item
            xs={12}
            sm={2}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={onSaveHandler}
              loading={isLoading}
            >
              Guardar
            </Button>
          </Grid>
        )}
      </Grid>
      <Box>
        <FormControlLabel
          label="Ingresar todo el día"
          control={
            <Checkbox
              checked={isAllDaySelected}
              onChange={onAllDaySelectedChangeHandler}
              name="isAllWorkdaySelected"
              color="primary"
              disabled={IS_READ_ONLY}
            />
          }
        />
      </Box>
    </Box>
  );
};

export default WorkdaysScheduleTable;
